import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import {
  USER_NAME_ALPHABET_CHARACTERS_ALLOWED,
  USER_NAME_ALPHABET_MAX_LENGTH,
  USER_NAME_KANA_MAX_LENGTH,
  USER_NAME_MAX_LENGTH,
  USER_PHONE_NUMBER_CHARACTERS_ALLOWED,
  USER_PHONE_NUMBER_MAX_LENGTH,
} from '../../../../domain/entity/UserEntity'
import {
  BasicInformatioTableDef,
  SearchOnEntitySearchCell,
} from '../../../containers/SingleSheetV2/table/model'
import { SelectOption } from '../../../model/selectOption'

// MIMEタイプではなく拡張子で指定する（FileUploadDialog が mime.getType で変換するため）。
const ICON_FILE_FORMAT = '.gif,.png,.jpg,.jpeg'

const ME_BASIC_INFORMATION_HEADER_ID = 'basicInformation'

// 部門・ロールは表示専用でオートコンプリートが disabled のため検索は実行されないが、
// ENTITY_SEARCH セルは検索関数を必須で要求するため空を返すものを渡す。
const NO_ENTITY_SEARCH: SearchOnEntitySearchCell = async () => []

// 選択肢はカスタム列挙の取得結果で、テーブル定義の関心事ではないため呼び出し側から渡す。
export type MeCellOptions = {
  timezoneOptions: SelectOption[]
  registrationStatusOptions: SelectOption[]
}

// ユーザー情報画面の基本情報テーブル。画面が1つしか無いため、セル定義と行構成を
// 分けずにここへまとめている。項目・並び・必須有無・桁数は機能プロパティ
// （function_properties）の me.* の定義に合わせている（property_layout の 1-19 の
// lastLoginAt は hidden_if_u が TRUE で表示しない項目のため持たない）。
// 編集できるのは本人が変更できる10項目だけで、残りは editable:false とする。
// 本人による変更は UpdateUserDelta の権限チェックで拒否されるか、更新APIの入力に
// 項目が無いため。
//
// テナント別のプロパティ上書き（function_property_configurations）は反映されない。
// 項目の必須有無・編集可否・ラベル・並びをここにハードコードしているため、同テーブルで
// hidden_if_u / required_if / editable_if_u やラベル・並びを変更しても画面に出ない。
// 最大文字数・許可文字は同テーブルの上書き対象外のため影響しない。
export const useMeBasicInformationTableDef = ({
  timezoneOptions,
  registrationStatusOptions,
}: MeCellOptions): BasicInformatioTableDef => {
  const intl = useIntl()
  return useMemo<BasicInformatioTableDef>(() => {
    const label = (id: string) => intl.formatMessage({ id })
    return {
      tables: [
        {
          header: {
            externalId: ME_BASIC_INFORMATION_HEADER_ID,
            label: label('meV2.basicInformation'),
          },
          rows: [
            [
              {
                externalId: 'me.code',
                field: 'code',
                label: label('meV2.code'),
                type: 'TEXT',
                width: 'full',
                required: false,
                editable: false,
              },
            ],
            [
              {
                externalId: 'me.iconUrl',
                field: 'iconUrl',
                label: label('meV2.iconUrl'),
                type: 'ICON',
                width: 'full',
                required: false,
                editable: true,
                fileFormat: ICON_FILE_FORMAT,
              },
            ],
            [
              {
                externalId: 'me.name',
                field: 'name',
                label: label('meV2.name'),
                type: 'TEXT',
                width: 'full',
                required: true,
                editable: true,
                placeholder: label('meV2.name.placeholder'),
                maxLength: USER_NAME_MAX_LENGTH,
              },
            ],
            [
              {
                externalId: 'me.nameKana',
                field: 'nameKana',
                label: label('meV2.nameKana'),
                type: 'TEXT',
                width: 'full',
                required: false,
                editable: true,
                placeholder: label('meV2.nameKana.placeholder'),
                maxLength: USER_NAME_KANA_MAX_LENGTH,
              },
            ],
            [
              {
                externalId: 'me.nameAlphabet',
                field: 'nameAlphabet',
                label: label('meV2.nameAlphabet'),
                type: 'TEXT',
                width: 'full',
                required: false,
                editable: true,
                placeholder: label('meV2.nameAlphabet.placeholder'),
                maxLength: USER_NAME_ALPHABET_MAX_LENGTH,
                charactersAllowed: USER_NAME_ALPHABET_CHARACTERS_ALLOWED,
              },
            ],
            [
              {
                externalId: 'me.email',
                field: 'email',
                label: label('meV2.email'),
                type: 'TEXT',
                width: 'full',
                required: true,
                // 変更は本人確認メールを経由するサブヘッダーのメールアドレス変更から
                // 行うため、単票では編集できない。
                editable: false,
              },
            ],
            [
              {
                externalId: 'me.phoneNumber',
                field: 'phoneNumber',
                label: label('meV2.phoneNumber'),
                type: 'TEXT',
                width: 'full',
                required: false,
                editable: true,
                placeholder: label('meV2.phoneNumber.placeholder'),
                maxLength: USER_PHONE_NUMBER_MAX_LENGTH,
                charactersAllowed: USER_PHONE_NUMBER_CHARACTERS_ALLOWED,
              },
            ],
            [
              {
                externalId: 'me.birthday',
                field: 'birthday',
                label: label('meV2.birthday'),
                type: 'DATE',
                width: 'full',
                required: false,
                editable: true,
              },
            ],
            [
              {
                externalId: 'me.timezoneId',
                field: 'timezoneId',
                label: label('meV2.timezoneId'),
                type: 'SELECT',
                width: 'full',
                required: true,
                editable: true,
                options: timezoneOptions,
                clearable: false,
              },
            ],
            [
              {
                externalId: 'me.division',
                field: 'division',
                label: label('meV2.division'),
                type: 'ENTITY_SEARCH',
                width: 'full',
                required: false,
                editable: false,
                search: NO_ENTITY_SEARCH,
                displayIcon: false,
              },
            ],
            [
              {
                externalId: 'me.role',
                field: 'role',
                label: label('meV2.role'),
                type: 'ENTITY_SEARCH',
                width: 'full',
                required: true,
                editable: false,
                search: NO_ENTITY_SEARCH,
                displayIcon: false,
              },
            ],
            [
              {
                externalId: 'me.permissionPolicies',
                field: 'permissionPolicies',
                label: label('meV2.permissionPolicies'),
                type: 'TEXT',
                width: 'full',
                required: false,
                editable: false,
              },
            ],
            [
              {
                externalId: 'me.hireLeaveDate',
                field: 'hireLeaveDate',
                label: label('meV2.hireLeaveDate'),
                type: 'DATE_TERM',
                width: 'full',
                required: false,
                editable: false,
              },
            ],
            [
              {
                externalId: 'me.expirationDate',
                field: 'expirationDate',
                label: label('meV2.expirationDate'),
                type: 'DATE_TIME_RANGE',
                width: 'full',
                required: false,
                editable: false,
              },
            ],
            [
              {
                externalId: 'me.emailNotification',
                field: 'emailNotification',
                label: label('meV2.emailNotification'),
                type: 'CHECKBOX',
                width: 'full',
                required: false,
                editable: true,
              },
            ],
            [
              {
                externalId: 'me.partnerMember',
                field: 'partnerMember',
                label: label('meV2.partnerMember'),
                type: 'CHECKBOX',
                width: 'full',
                required: false,
                editable: false,
              },
            ],
            [
              {
                externalId: 'me.registrationStatus',
                field: 'registrationStatus',
                label: label('meV2.registrationStatus'),
                type: 'SELECT',
                width: 'full',
                required: false,
                editable: false,
                options: registrationStatusOptions,
              },
            ],
            [
              {
                externalId: 'me.oneTimeTokenExpiredAt',
                field: 'oneTimeTokenExpiredAt',
                label: label('meV2.oneTimeTokenExpiredAt'),
                type: 'DATE_TIME',
                width: 'full',
                required: false,
                editable: false,
              },
            ],
            [
              {
                externalId: 'me.slackUser',
                field: 'slackUser',
                label: label('meV2.slackUser'),
                type: 'SLACK_USER',
                width: 'full',
                required: false,
                editable: true,
              },
            ],
            [
              {
                externalId: 'me.githubUserName',
                field: 'githubUserName',
                label: label('meV2.githubUserName'),
                type: 'GITHUB_USER_NAME',
                width: 'full',
                required: false,
                editable: true,
              },
            ],
          ],
        },
      ],
    }
  }, [intl, timezoneOptions, registrationStatusOptions])
}
